import { useCallback, useEffect, useRef } from 'react';
import type { PurchaseInvoiceDetailRow, PurchaseInvoiceFieldValue, PurchaseInvoiceLookup } from '../../../types';
import type { TableSelectedCell } from '@/components/custom/erp/input-table-v2';
import { useTableRows } from '@/components/custom/erp/input-table-v2';
import { roundNumber } from '@/lib/formatUtils';

export type SelectedCellInfo = TableSelectedCell;

export type DetailRow = PurchaseInvoiceDetailRow;

type PriceLookupMeta = {
  isInitializing: boolean;
};

type PriceLookupHandler = (
  row: DetailRow,
  field: string,
  value: PurchaseInvoiceFieldValue,
  updateRow: (rowUuid: string, fields: Partial<DetailRow>) => void,
  meta: PriceLookupMeta
) => void;

/* ================= HOOK ================= */

export function useDetailRows(
  initialRows: DetailRow[] = [],
  loai_ck: string = '1',
  ck_tl_nt: number = 0,
  priceLookupHandler?: PriceLookupHandler
) {
  const priceLookupHandlerRef = useRef<PriceLookupHandler | undefined>(priceLookupHandler);

  const isInitializingRef = useRef(true);

  useEffect(() => {
    priceLookupHandlerRef.current = priceLookupHandler;
  }, [priceLookupHandler]);

  useEffect(() => {
    isInitializingRef.current = false;
  }, []);

  const handleFieldValueChange = (
    row: DetailRow,
    field: string,
    value: PurchaseInvoiceFieldValue
  ): Partial<DetailRow> => {
    const extra: Partial<DetailRow> = {};

    if (field === 'gia_nt0') {
      const so_luong = Number(row.so_luong) || 1;
      const gia = Number(value) || 0;
      const tien_tck_nt = roundNumber(gia * so_luong);
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 100) / 100;

      extra.tien_tck_nt = tien_tck_nt;
      extra.ck_nt = ck_nt;
      extra.tien_nt0 = Math.round((tien_tck_nt - ck_nt) * 100) / 100;
    }

    if (field === 'so_luong') {
      const gia = Number(row.gia_nt0) || 0;
      const so_luong = Number(value) || 0;
      const tien_tck_nt = roundNumber(gia * so_luong);
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 100) / 100;

      extra.tien_tck_nt = tien_tck_nt;
      extra.ck_nt = ck_nt;
      extra.tien_nt0 = Math.round((tien_tck_nt - ck_nt) * 100) / 100;
    }

    if (field === 'tien_nt0') {
      const tien = Number(value) || 0;
      const so_luong = Number(row.so_luong) || 0;
      if (so_luong > 0) {
        extra.gia_nt0 = Math.round((tien / so_luong) * 100) / 100;
      }
    }
    if (field === 'tl_ck' && loai_ck === '1') {
      const tien_tck_nt = Number(row.tien_tck_nt) || 0;
      const tl_ck = Number(value) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 100) / 100;
      const tien_nt0 = Math.round((tien_tck_nt - ck_nt) * 100) / 100;
      const taxRate = row.ma_thue_data?.thue_suat || 0;

      extra.ck_nt = ck_nt;
      extra.tien_nt0 = tien_nt0;
      extra.thue_nt = (tien_nt0 * taxRate) / 100;
    }

    if (field === 'ck_nt' && loai_ck === '1') {
      const tien_tck_nt = Number(row.tien_tck_nt) || 0;
      const ck_nt = Number(value) || 0;
      const tl_ck = tien_tck_nt > 0 ? Math.round((ck_nt / tien_tck_nt) * 100 * 100) / 100 : 0;
      const tien_nt0 = Math.round((tien_tck_nt - ck_nt) * 100) / 100;
      const taxRate = row.ma_thue_data?.thue_suat || 0;

      extra.ck_nt = ck_nt;
      extra.tl_ck = tl_ck;
      extra.tien_nt0 = tien_nt0;
      extra.thue_nt = (tien_nt0 * taxRate) / 100;
    }

    if (field === 'tien_tck_nt') {
      const tien_tck_nt = Number(value) || 0;
      const tl_ck = Number(row.tl_ck) || 0;
      const ck_nt = Math.round(((tien_tck_nt * tl_ck) / 100) * 100) / 100;
      const tien_nt0 = Math.round((tien_tck_nt - ck_nt) * 100) / 100;
      const taxRate = row.ma_thue_data?.thue_suat || 0;

      extra.ck_nt = ck_nt;
      extra.tien_nt0 = tien_nt0;
      extra.thue_nt = (tien_nt0 * taxRate) / 100;
    }

    const shouldCalcTax = ['gia_nt0', 'tien_nt0', 'ma_thue_data'].includes(field);
    if (shouldCalcTax) {
      const base = field === 'tien_nt0' ? Number(value) : (row.gia_nt0 || 0) * (row.so_luong || 0);

      const taxRate =
        field === 'ma_thue_data'
          ? (value as PurchaseInvoiceLookup)?.thue_suat || (value as PurchaseInvoiceLookup)?.tax_rate || 0
          : row.ma_thue_data?.thue_suat || row.ma_thue_data?.tax_rate || 0;

      extra.thue_nt = (base * taxRate) / 100;
    }

    if (field === 'ma_vt_data') {
      const lookupValue = value as PurchaseInvoiceLookup;
      extra.dvt = lookupValue?.dvt_nl_data?.uuid || lookupValue?.dvt_data?.uuid || row.dvt;
    }

    return extra;
  };

  const {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange: baseHandleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    setNonWritableFields,
    setPasteConfigs,
    isPasting
  } = useTableRows<DetailRow>({
    initialRows,
    defaultRows: 1,
    onFieldValueChange: handleFieldValueChange,
    defaultField: 'ma_vt'
  });

  const updateRowByUuid = useCallback(
    (uuid: string, fields: Partial<DetailRow>) => {
      setRows(prev => prev.map(r => (r.uuid === uuid ? { ...r, ...fields } : r)));
    },
    [setRows]
  );

  const setPriceLookupHandler = useCallback((handler?: PriceLookupHandler) => {
    priceLookupHandlerRef.current = handler;
  }, []);

  const handleCellValueChange = useCallback(
    (rowUuid: string, field: string, newValue: PurchaseInvoiceFieldValue) => {
      baseHandleCellValueChange(rowUuid, field, newValue);

      const handler = priceLookupHandlerRef.current;
      if (!handler) return;

      setRows(prevRows => {
        const row = prevRows.find(r => r.uuid === rowUuid);
        if (!row) return prevRows;

        const updatedRow = { ...row, [field]: newValue };

        handler(updatedRow, field, newValue, updateRowByUuid, {
          isInitializing: isInitializingRef.current
        });

        return prevRows;
      });
    },
    [baseHandleCellValueChange, setRows, updateRowByUuid]
  );

  useEffect(() => {
    if (!rows.length) return;

    setRows(prev =>
      prev.map(row => {
        const tien_raw = roundNumber((row.gia_nt0 || 0) * (row.so_luong || 0));

        let tl_ck = row.tl_ck || 0;
        let ck_nt = row.ck_nt || 0;

        if (loai_ck === '2') {
          tl_ck = ck_tl_nt;
          ck_nt = Math.round(((tien_raw * tl_ck) / 100) * 100) / 100;
        } else if (loai_ck === '3') {
          ck_nt = ck_tl_nt;
          tl_ck = 0;
        }

        const tien_nt0 = Math.round((tien_raw - ck_nt) * 100) / 100;
        const taxRate = row.ma_thue_data?.thue_suat || row.ma_thue_data?.tax_rate || 0;

        return {
          ...row,
          tien_tck_nt: tien_raw,
          ck_nt,
          tl_ck,
          tien_nt0,
          thue_nt: (tien_nt0 * taxRate) / 100
        };
      })
    );
  }, [loai_ck, ck_tl_nt, setRows, rows.length]);

  return {
    rows,
    setRows,
    selectedRowUuid,
    setSelectedRowUuid,
    selectedRow,
    setSelectedRow,
    selectedCell,
    setSelectedCell,

    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,

    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,

    setNonWritableFields,
    setPasteConfigs,
    isPasting,

    setPriceLookupHandler
  };
}
